import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  AUTH_TOKEN_COOKIE,
  AUTH_USER_TYPE_COOKIE,
  buildApiUrl,
  clearAuthCookies,
  decodeJwtPayload,
  getCookie,
  isTokenExpired,
  setAccessToken,
  setCookie,
} from '@/lib/api/client';
import { logoutApiAuthLogoutPost } from '@/lib/api/generated';
import { clearSidebarCollapsed } from '@/lib/sidebar-collapse';

export interface AuthUser {
  id: string;
  email: string;
  /** BUG-883: server-resolved display name (staff.name > displayName > email
   *  local-part). Carried as a JWT claim so the header needs no extra request. */
  name: string;
  loginId?: string | null;
  userType: string;
  staffId?: string | null;
  outletId?: string | null;
}

interface LoginResponse {
  accessToken: string;
  tokenType: string;
}

interface AuthContextValue {
  user: AuthUser | null;
  isLoading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

/**
 * Build the authenticated principal from the access-token claims. The backend
 * `/api/auth/me` deliberately returns only `{ outletId }` (conformance: it is
 * byte-identical to the legacy route), so identity lives in the JWT — `sub`,
 * `userType`, `email`, `loginId`, `staffId`, `outletId`. Returns null for a
 * malformed token or one missing the mandatory `sub`/`userType` claims.
 *
 * Spec B: `email` is now optional (email-less, loginId-only accounts exist), so
 * a missing `email` claim is tolerated (falls back to `''`). The optional
 * `loginId` claim carries the account handle so the header/principal can show it
 * when `email` is absent.
 */
function userFromToken(token: string): AuthUser | null {
  const claims = decodeJwtPayload(token);
  if (
    !claims ||
    typeof claims.sub !== 'string' ||
    typeof claims.userType !== 'string'
  ) {
    return null;
  }

  return {
    id: claims.sub,
    email: typeof claims.email === 'string' ? claims.email : '',
    // Older tokens issued before BUG-883 carry no `name`; fall back to the
    // previous behaviour so an existing session doesn't render blank.
    name:
      typeof claims.name === 'string' && claims.name
        ? claims.name
        : typeof claims.email === 'string'
          ? claims.email.split('@')[0]
          : '',
    loginId: typeof claims.loginId === 'string' ? claims.loginId : null,
    userType: claims.userType,
    staffId: typeof claims.staffId === 'string' ? claims.staffId : null,
    outletId: typeof claims.outletId === 'string' ? claims.outletId : null,
  };
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const syncAuthenticatedUser = useCallback((nextUser: AuthUser | null) => {
    setUser(nextUser);

    if (nextUser?.userType) {
      setCookie(AUTH_USER_TYPE_COOKIE, nextUser.userType);
    } else {
      clearAuthCookies();
    }
  }, []);

  const clearAuthState = useCallback(() => {
    setAccessToken(null);
    clearAuthCookies();
    setUser(null);
    // GEN-04-04: the collapsed-menu choice is remembered only until sign-out.
    clearSidebarCollapsed();
  }, []);

  useEffect(() => {
    const bootstrap = async () => {
      const token = getCookie(AUTH_TOKEN_COOKIE) ?? null;

      if (!token || isTokenExpired(token)) {
        if (token) clearAuthState();
        setIsLoading(false);
        return;
      }

      const nextUser = userFromToken(token);
      if (nextUser) {
        setAccessToken(token);
        syncAuthenticatedUser(nextUser);
      } else {
        clearAuthState();
      }

      setIsLoading(false);
    };

    void bootstrap();
  }, [clearAuthState, syncAuthenticatedUser]);

  useEffect(() => {
    const handleLogout = () => {
      clearAuthState();
    };

    window.addEventListener('yakson-auth:logout', handleLogout);
    return () => window.removeEventListener('yakson-auth:logout', handleLogout);
  }, [clearAuthState]);

  const login = useCallback(
    async (username: string, password: string) => {
      const body = new URLSearchParams();
      body.set('username', username);
      body.set('password', password);

      const response = await fetch(buildApiUrl('/api/auth/login'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      if (!response.ok) {
        let message = 'Login failed';

        try {
          const errorBody = (await response.json()) as { detail?: string; message?: string };
          message = errorBody.detail || errorBody.message || message;
        } catch {
          // Ignore parse failures and use the fallback message.
        }

        throw new Error(message);
      }

      const data = (await response.json()) as LoginResponse;

      const nextUser = userFromToken(data.accessToken);
      if (!nextUser) {
        clearAuthState();
        throw new Error('Login failed: malformed authentication token');
      }

      setAccessToken(data.accessToken);
      setCookie(AUTH_TOKEN_COOKIE, data.accessToken);
      syncAuthenticatedUser(nextUser);
    },
    [clearAuthState, syncAuthenticatedUser],
  );

  const logout = useCallback(() => {
    // GEN-15: tell the server first so the Audit trail shows the sign-out. The
    // request starts before the token is dropped and never blocks signing out.
    void logoutApiAuthLogoutPost({ keepalive: true }).catch(() => undefined);
    clearAuthState();
  }, [clearAuthState]);

  const value = useMemo(
    () => ({ user, isLoading, login, logout }),
    [isLoading, login, logout, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}
